import { useState, useEffect, useCallback, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  $createParagraphNode,
  $getRoot,
  EditorState,
  LexicalEditor,
  $isElementNode,
} from 'lexical';
import { $createHeadingNode, HeadingNode, HeadingTagType } from '@lexical/rich-text';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  ListItemNode,
} from '@lexical/list';
import { $setBlocksType } from '@lexical/selection';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { LinkNode } from '@lexical/link';
import { supabase } from '@/lib/supabase';

// ─── Toolbar ────────────────────────────────────────────────────────────────

interface ToolbarProps {
  onInsertImage: () => void;
  uploading: boolean;
}

function Toolbar({ onInsertImage, uploading }: ToolbarProps) {
  const [editor] = useLexicalComposerContext();

  const formatHeading = (tag: HeadingTagType) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createHeadingNode(tag));
      }
    });
  };

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatText = (format: 'bold' | 'italic' | 'underline') => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const insertList = (type: 'bullet' | 'number') => {
    if (type === 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  };

  const btn = 'w-8 h-8 flex items-center justify-center rounded-lg hover:bg-orange-50 hover:text-orange-500 text-gray-600 cursor-pointer transition-colors text-sm font-medium';
  const sep = <div className="w-px h-5 bg-gray-200 mx-1" />;

  return (
    <div className="flex items-center flex-wrap gap-0.5 px-3 py-2 border-b border-gray-200 bg-gray-50 rounded-t-xl">
      <button type="button" onClick={formatParagraph} className={btn} title="正文">
        <span className="text-xs">P</span>
      </button>
      <button type="button" onClick={() => formatHeading('h1')} className={btn} title="標題 1">
        <span className="text-xs font-bold">H1</span>
      </button>
      <button type="button" onClick={() => formatHeading('h2')} className={btn} title="標題 2">
        <span className="text-xs font-bold">H2</span>
      </button>
      <button type="button" onClick={() => formatHeading('h3')} className={btn} title="標題 3">
        <span className="text-xs font-bold">H3</span>
      </button>
      {sep}
      <button type="button" onClick={() => formatText('bold')} className={btn} title="粗體 (Ctrl+B)">
        <i className="ri-bold" />
      </button>
      <button type="button" onClick={() => formatText('italic')} className={btn} title="斜體 (Ctrl+I)">
        <i className="ri-italic" />
      </button>
      <button type="button" onClick={() => formatText('underline')} className={btn} title="底線 (Ctrl+U)">
        <i className="ri-underline" />
      </button>
      {sep}
      <button type="button" onClick={() => insertList('bullet')} className={btn} title="無序清單">
        <i className="ri-list-unordered" />
      </button>
      <button type="button" onClick={() => insertList('number')} className={btn} title="有序清單">
        <i className="ri-list-ordered" />
      </button>
      {sep}
      <button
        type="button"
        onClick={onInsertImage}
        disabled={uploading}
        className={`${btn} ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        title="插入圖片"
      >
        {uploading
          ? <i className="ri-loader-4-line animate-spin" />
          : <i className="ri-image-add-line" />}
      </button>
    </div>
  );
}

// ─── InitialContent Plugin ───────────────────────────────────────────────────

function InitialContentPlugin({ htmlContent }: { htmlContent: string }) {
  const [editor] = useLexicalComposerContext();
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current || !htmlContent) return;
    initialized.current = true;
    editor.update(() => {
      try {
        const parser = new DOMParser();
        const dom = parser.parseFromString(htmlContent, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);
        const root = $getRoot();
        root.clear();
        // Wrap any non-block (inline) nodes in a paragraph to avoid error #282
        const blockNodes = nodes.map((node) => {
          if ($isElementNode(node) && !node.isInline()) {
            return node;
          }
          const para = $createParagraphNode();
          para.append(node);
          return para;
        });
        if (blockNodes.length > 0) {
          root.append(...blockNodes);
        } else {
          root.append($createParagraphNode());
        }
      } catch (e) {
        console.error('RichTextEditor: failed to load initial content', e);
      }
    });
  }, [editor, htmlContent]);

  return null;
}

// ─── ImageInsert Plugin ──────────────────────────────────────────────────────

interface ImageInsertPluginProps {
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onUpload: (file: File) => Promise<string | null>;
}

function ImageInsertPlugin({ fileInputRef, onUpload }: ImageInsertPluginProps) {
  const [editor] = useLexicalComposerContext();

  const handleFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await onUpload(file);
    if (!url) return;
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const imgHtml = `<p><img src="${url}" alt="圖片" style="max-width:100%;border-radius:8px;margin:8px 0;" /></p>`;
        const parser = new DOMParser();
        const dom = parser.parseFromString(imgHtml, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);
        selection.insertNodes(nodes);
      }
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [editor, onUpload, fileInputRef]);

  return (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/jpeg,image/png,image/webp,image/gif"
      className="hidden"
      onChange={handleFile}
    />
  );
}

// ─── Main Editor ─────────────────────────────────────────────────────────────

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

const editorTheme = {
  heading: {
    h1: 'rte-h1',
    h2: 'rte-h2',
    h3: 'rte-h3',
  },
  text: {
    bold: 'font-bold',
    italic: 'italic',
    underline: 'underline',
  },
  list: {
    ul: 'rte-ul',
    ol: 'rte-ol',
    listitem: 'rte-li',
  },
  paragraph: 'rte-p',
};

export default function RichTextEditor({ value, onChange, placeholder = '請輸入文章內容...' }: RichTextEditorProps) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleUpload = useCallback(async (file: File): Promise<string | null> => {
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) return null;
    if (file.size > 5 * 1024 * 1024) return null;
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `content-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage
        .from('news-images')
        .upload(fileName, file, { cacheControl: '3600', upsert: false });
      if (error) throw error;
      const { data } = supabase.storage.from('news-images').getPublicUrl(fileName);
      return data.publicUrl;
    } catch {
      return null;
    } finally {
      setUploading(false);
    }
  }, []);

  const handleChange = useCallback((editorState: EditorState, editor: LexicalEditor) => {
    editorState.read(() => {
      const html = $generateHtmlFromNodes(editor, null);
      onChange(html);
    });
  }, [onChange]);

  const initialConfig = {
    namespace: 'NewsEditor',
    theme: editorTheme,
    nodes: [HeadingNode, ListNode, ListItemNode, LinkNode],
    onError: (err: Error) => console.error(err),
  };

  return (
    <>
      <style>{`
        .rte-h1 { font-size: 1.5rem; font-weight: 700; margin: 0.75rem 0; }
        .rte-h2 { font-size: 1.25rem; font-weight: 700; margin: 0.5rem 0; }
        .rte-h3 { font-size: 1.1rem; font-weight: 600; margin: 0.5rem 0; }
        .rte-p  { margin: 0.25rem 0; }
        .rte-ul { list-style: disc; padding-left: 1.5rem; margin: 0.5rem 0; }
        .rte-ol { list-style: decimal; padding-left: 1.5rem; margin: 0.5rem 0; }
        .rte-li { margin: 0.2rem 0; }
      `}</style>
      <LexicalComposer initialConfig={initialConfig}>
        <div className="border border-gray-200 rounded-xl overflow-hidden focus-within:border-orange-400 transition-colors">
          <Toolbar onInsertImage={() => fileInputRef.current?.click()} uploading={uploading} />
          <div className="relative min-h-[300px]">
            <RichTextPlugin
              contentEditable={
                <ContentEditable className="outline-none px-4 py-3 min-h-[300px] text-sm text-gray-800 leading-relaxed" />
              }
              placeholder={
                <div className="absolute top-3 left-4 text-gray-400 text-sm pointer-events-none select-none">
                  {placeholder}
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <HistoryPlugin />
            <ListPlugin />
            <OnChangePlugin onChange={handleChange} />
            <InitialContentPlugin htmlContent={value} />
            <ImageInsertPlugin fileInputRef={fileInputRef} onUpload={handleUpload} />
          </div>
        </div>
      </LexicalComposer>
    </>
  );
}
